import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import { authService } from '../services/authService';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import Card from '../components/UI/Card';
import { ApiService } from '../services/api';

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CallbackContent = styled.div`
  text-align: center;
  padding: 2rem;
`;

const StatusIcon = styled.div<{ theme: any; status: 'loading' | 'success' | 'error' }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  background: ${props => {
    switch (props.status) {
      case 'loading': return props.theme.colors.primary + '20';
      case 'success': return props.theme.colors.success + '20';
      case 'error': return props.theme.colors.error + '20';
      default: return props.theme.colors.border + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'loading': return props.theme.colors.primary;
      case 'success': return props.theme.colors.success;
      case 'error': return props.theme.colors.error;
      default: return props.theme.colors.text;
    }
  }};
`;

const SpinningLoader = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const Message = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const AuthCallbackPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');

  usePageTitle('Authentication');

  useEffect(() => {
    // Guard against double invocation (React StrictMode/dev hot reload)
    if ((window as any).__OIDC_CALLBACK_HANDLED__) {
      return;
    }
    (window as any).__OIDC_CALLBACK_HANDLED__ = true;

    const handleCallback = async () => {
      try {
        setStatus('loading');
        setMessage('Processing authentication...');

        // Handle the OIDC callback
        const user = await authService.handleCallback();

        // Extract 13-digit Tax ID from token/profile (robust)
        const extractTaxId = (profile: any): string | null => {
          const candidates = [
            profile?.taxId,
            profile?.tax_id,
            profile?.tin,
            profile?.ntid,
          ];
          for (const c of candidates) {
            if (typeof c === 'string' && /^\d{13}$/.test(c)) return c;
          }
          // Fallback: scan all string values for a 13-digit code
          for (const v of Object.values(profile || {})) {
            if (typeof v === 'string' && /^\d{13}$/.test(v)) return v;
          }
          return null;
        };

        const taxId = extractTaxId(user?.profile);
        if (!taxId) {
          // Not a consultant token (missing required claim)
          sessionStorage.setItem('loginGateReason', 'not_consultant');
          sessionStorage.setItem('loginGateMessage', 'This account is not registered as a Tax Consultant.');
          await authService.clearLocalUser();
          navigate('/', { replace: true });
          return;
        }

        // Gate login based on consultant registration status
        try {
          const track = await ApiService.trackRegistrationByTaxId(taxId);
          const statusUpper = (track.status || '').toUpperCase();

          if (statusUpper !== 'APPROVED') {
            sessionStorage.setItem('loginGateReason', 'pending');
            sessionStorage.setItem('loginGateStatus', track.status || '');
            sessionStorage.setItem('loginGateTaxId', track.taxId || taxId);
            await authService.clearLocalUser();
            navigate('/', { replace: true });
            return;
          }
        } catch (e: any) {
          // If no consultant found for this Tax ID, block login
          if (e?.status === 404 || e?.payload?.error === 'not_found' || (e?.message || '') === 'not_found') {
            sessionStorage.setItem('loginGateReason', 'not_consultant');
            sessionStorage.setItem('loginGateTaxId', taxId);
            sessionStorage.setItem('loginGateMessage', 'No Tax Consultant registration was found for this Tax ID.');
            await authService.clearLocalUser();
            navigate('/', { replace: true });
            return;
          }
          throw e;
        }

        // Fetch consultant details for the logged in user (Bearer access token)
        try {
          if (user?.access_token) {
            await ApiService.getConsultantMe(user.access_token);
          } else {
            throw new Error('Missing access token');
          }
        } catch (e: any) {
          console.warn('Unable to fetch /consultants/me after login:', e);
          sessionStorage.setItem('loginGateReason', 'profile_error');
          sessionStorage.setItem(
            'loginGateMessage',
            'We could not load your consultant profile. Please try again.',
          );
          sessionStorage.setItem('loginGateTaxId', taxId);
          await authService.clearLocalUser();
          navigate('/', { replace: true });
          return;
        }
        
        setStatus('success');
        setMessage('Authentication successful! Redirecting to dashboard...');
        
        showToast(toast.success(
          'Login Successful',
          `Welcome back, ${user.profile.name || user.profile.email}!`
        ));

        // Redirect to dashboard or intended page after a short delay
        setTimeout(() => {
          const intendedPath = sessionStorage.getItem('intendedPath') || '/dashboard';
          sessionStorage.removeItem('intendedPath');
          navigate(intendedPath, { replace: true });
        }, 2000);

      } catch (error) {
        console.error('Authentication callback error:', error);
        
        setStatus('error');
        setMessage('Authentication failed. Please try logging in again.');
        
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        
        showToast(toast.error(
          'Authentication Failed',
          errorMessage
        ));

        // Don't auto-redirect on error so user can see debug info
        // setTimeout(() => {
        //   navigate('/', { replace: true });
        // }, 3000);
      }
    };

    handleCallback();
  }, [navigate, showToast]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <SpinningLoader size={32} />;
      case 'success':
        return <CheckCircle size={32} />;
      case 'error':
        return <XCircle size={32} />;
      default:
        return <SpinningLoader size={32} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return 'Authenticating...';
      case 'success':
        return 'Authentication Successful';
      case 'error':
        return 'Authentication Failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <Container>
      <Card>
        <CallbackContent>
          <StatusIcon theme={theme} status={status}>
            {getStatusIcon()}
          </StatusIcon>
          <Title theme={theme}>
            {getTitle()}
          </Title>
          <Message theme={theme}>
            {message}
          </Message>
        </CallbackContent>
      </Card>
      
    </Container>
  );
};

export default AuthCallbackPage;
